function handlePageTitleAnimation() {
  // Null check for document.title
  if (!document || typeof document.title !== 'string') {
    console.error('Document title not available - page title animation disabled');
    return {
      startInactivityTimer: () => {},
      resetInactivityTimer: () => {},
      stopTitleAnimation: () => {},
      cleanup: () => {}
    };
  }

  const originalTitle = document.title;

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    console.log('User prefers reduced motion - page title animation disabled');
    return {
      startInactivityTimer: () => {},
      resetInactivityTimer: () => {},
      stopTitleAnimation: () => {},
      cleanup: () => {}
    };
  }

  const inactivityMessages = [
    "🛏️ Rest assured, we have amazing deals!",
    "💺 Your perfect seat is waiting!",
    "🏠 Make yourself at home... keep looking!",
    "🛋️ Take your time, we'll wait!",
    "✨ Still finding your perfect piece?",
    "🏠 Your dream furniture is here somewhere!"
  ];

  const leftPageMessages = [
    "🛋️ Your dream sofa is getting away",
    "🏠 Home is calling you back",
    "💕 We miss you already",
    "🛋️ Your comfort zone awaits",
    "🏠 Don't leave your perfect home behind",
  ];

  let animationInterval;
  let inactivityTimer;
  let isInactive = false;
  let tabSwitchDelayTimer;
  let currentAnimationMessage = "";
  let showingOriginalTitle = true;
  let animationDelay = 2000; // 2 seconds
  let inactivityDelay = 30000; // 30 seconds
  let tabSwitchDelay = 10000; // 10 seconds
  let lastMouseMoveTime = 0;
  let mouseMoveThrottleDelay = 100; // 100ms throttle
  let eventListeners = []; // Store listeners for cleanup

  function startTitleAnimation(pageTitle) {
    if (!pageTitle || typeof pageTitle !== 'string') {
      console.error('Invalid page title provided to animation');
      return;
    }

    if (animationInterval) {
      clearInterval(animationInterval);
    }

    // Store the message and reset toggle state
    currentAnimationMessage = pageTitle;
    showingOriginalTitle = true;

    // Start animating the title
    animationInterval = setInterval(() => {
      try {
        if (showingOriginalTitle) {
          document.title = currentAnimationMessage;
          showingOriginalTitle = false;
        } else {
          document.title = originalTitle;
          showingOriginalTitle = true;
        }
      } catch (error) {
        console.error('Error updating document title:', error);
        stopTitleAnimation();
      }
    }, animationDelay);
  }

  function stopTitleAnimation() {
    if (animationInterval) {
      clearInterval(animationInterval);
      animationInterval = null;
    }

    // Clear the delay timer if it's running
    if (tabSwitchDelayTimer) {
      clearTimeout(tabSwitchDelayTimer);
      tabSwitchDelayTimer = null;
    }

    // Restore the original title and reset state
    document.title = originalTitle;
    isInactive = false;
    showingOriginalTitle = true;
    currentAnimationMessage = "";
  }

  function cleanup() {
    stopTitleAnimation();
    
    // Clear inactivity timer
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      inactivityTimer = null;
    }

    // Remove all event listeners
    eventListeners.forEach(({ element, event, handler, options }) => {
      element.removeEventListener(event, handler, options);
    });
    eventListeners = [];
  }

  function startInactivityTimer() {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    // Don't start inactivity timer if page is already hidden
    if (document.hidden) {
      return;
    }

    // Set timer for 30 seconds
    inactivityTimer = setTimeout(() => {
      handleInactivity();
    }, inactivityDelay);
  }

  function resetInactivityTimer() {
    // Clear existing timer
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    // If user is inactive, stop the animation and reset state
    if (isInactive) {
      stopTitleAnimation();
    }

    // Start new timer (unless page is hidden)
    if (!document.hidden) {
      startInactivityTimer();
    }
  }

  function handleInactivity() {
    // Don't start animation if page is already hidden
    if (document.hidden) {
      return;
    }

    const pageTitle = inactivityMessages[Math.floor(Math.random() * inactivityMessages.length)];
    isInactive = true;
    startTitleAnimation(pageTitle);
  }

  function startDelayedTitleAnimation() {
    if (tabSwitchDelayTimer) {
      clearTimeout(tabSwitchDelayTimer);
    }

    // Don't start animation if already running or user is inactive
    if (animationInterval || isInactive) {
      return;
    }
    
    // Set timer for 10 seconds
    tabSwitchDelayTimer = setTimeout(() => {
      // Only start animation if page is still hidden
      if (document.hidden) {
        const pageTitle = leftPageMessages[Math.floor(Math.random() * leftPageMessages.length)];
        startTitleAnimation(pageTitle);
      }
      tabSwitchDelayTimer = null;
    }, tabSwitchDelay);
  }

  // Store event listeners for cleanup
  const visibilityChangeHandler = () => {
    if (document.hidden) {
      // Clear inactivity timer since tab switching takes priority
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      startDelayedTitleAnimation();
    } else {
      // Cancel delay timer if user returns before delay expires
      if (tabSwitchDelayTimer) {
        clearTimeout(tabSwitchDelayTimer);
        tabSwitchDelayTimer = null;
      }
      stopTitleAnimation();
      // Resume inactivity tracking when page becomes visible
      startInactivityTimer();
    }
  };

  document.addEventListener("visibilitychange", visibilityChangeHandler);
  eventListeners.push({
    element: document,
    event: "visibilitychange", 
    handler: visibilityChangeHandler,
    options: undefined
  });

  // Throttled mousemove handler for performance
  function throttledMouseMoveHandler() {
    const now = Date.now();
    if (now - lastMouseMoveTime >= mouseMoveThrottleDelay) {
      lastMouseMoveTime = now;
      resetInactivityTimer();
    }
  }

  // Add activity event listeners
  const activityEvents = ['keydown', 'click', 'scroll', 'touchstart'];
  activityEvents.forEach(event => {
    document.addEventListener(event, resetInactivityTimer, { passive: true });
    eventListeners.push({
      element: document,
      event: event,
      handler: resetInactivityTimer,
      options: { passive: true }
    });
  });
  
  // Add throttled mousemove listener separately
  document.addEventListener('mousemove', throttledMouseMoveHandler, { passive: true });
  eventListeners.push({
    element: document,
    event: 'mousemove',
    handler: throttledMouseMoveHandler,
    options: { passive: true }
  });

  // Return object with methods for external access
  return {
    startInactivityTimer,
    resetInactivityTimer,
    stopTitleAnimation,
    cleanup
  };
}


document.addEventListener("DOMContentLoaded", () => {
  const titleAnimation = handlePageTitleAnimation();
  
  // Start inactivity tracking when page loads (if not hidden)
  if (!document.hidden) {
    titleAnimation.startInactivityTimer();
  }
});